import type { ApiErrorBody } from "@gridaction/core";
import { generalizePath, trackEvent } from "./analytics";
import { API_ORIGIN, isNativeApp } from "./platform";
import { sessionToken } from "./session-token";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }

  get isUpgradeRequired() {
    return this.code === "upgrade_required";
  }
}

// Conditional GET cache: the API returns ETags for boards so polling costs a 304.
const etags = new Map<string, { etag: string; data: unknown }>();

/** Where an API path lives, and how this client proves who it is. */
export function apiUrl(path: string): string {
  return `${API_ORIGIN}/api/v1${path}`;
}

export function authHeaders(): Record<string, string> {
  const token = sessionToken();
  return token ? { authorization: `Bearer ${token}` } : {};
}

// The website signs in with its cookie; the apps never send cookies at all.
const credentials: RequestCredentials = isNativeApp ? "omit" : "same-origin";

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const url = apiUrl(path);
  const headers: Record<string, string> = { accept: "application/json", ...authHeaders() };
  let payload: BodyInit | undefined;
  if (body instanceof FormData) {
    payload = body;
  } else if (body !== undefined) {
    headers["content-type"] = "application/json";
    payload = JSON.stringify(body);
  }
  const cached = method === "GET" ? etags.get(url) : undefined;
  if (cached) headers["if-none-match"] = cached.etag;

  let response: Response;
  try {
    response = await fetch(url, { method, headers, body: payload, credentials });
  } catch {
    throw new ApiError(
      0,
      "network_error",
      "You appear to be offline. Check your connection and try again.",
    );
  }

  if (response.status === 304 && cached) return cached.data as T;
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const data = text ? (JSON.parse(text) as unknown) : undefined;
  if (!response.ok) {
    const error = (data as ApiErrorBody | undefined)?.error;
    // Hitting a Premium wall is the moment a person meets the upgrade — which
    // feature did it is the most useful thing to know about it.
    if (error?.code === "upgrade_required") {
      trackEvent("app.upgrade_prompted", { reason: generalizePath(path) });
    }
    throw new ApiError(
      response.status,
      error?.code ?? "request_failed",
      error?.message ?? "Something went wrong. Please try again.",
      error?.details,
    );
  }
  const etag = response.headers.get("etag");
  if (method === "GET" && etag) etags.set(url, { etag, data });
  return data as T;
}

export { credentials as apiCredentials };

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body: unknown = {}) => request<T>("POST", path, body),
  patch: <T>(path: string, body: unknown) => request<T>("PATCH", path, body),
  put: <T>(path: string, body: unknown) => request<T>("PUT", path, body),
  delete: <T>(path: string, body?: unknown) => request<T>("DELETE", path, body),
  clearCache: () => etags.clear(),
};

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Something went wrong. Please try again.";
}
